/**
 * One request should read as one transaction, named after its route template.
 *
 * WHY THIS IS A SEND-TIME HOOK AND NOT AN INTEGRATION OPTION. Two
 * instrumentations produce a server span for every request. Sentry's
 * `httpIntegration` creates the ROOT, which is what becomes the transaction,
 * and Next 16's own OpenTelemetry instrumentation creates two more underneath
 * it. Only Next's carries `http.route`, so the transaction is named from the
 * raw path: `GET /api/native/lesson/1`, `GET /api/native/lesson/2`, one
 * transaction name per lesson id, on the primary grouping key in Sentry.
 *
 * Suppressing Sentry's incoming span with
 * `httpIntegration({ ignoreIncomingRequests: () => true })` looks like the
 * cleaner fix and is not. Measured against a running server rather than
 * reasoned about: it splits one request across three trace ids, because
 * Sentry's span is what anchors the request's trace, and without it the
 * middleware span and each of Next's two spans each become their own root.
 * The job carrier in `lib/jobs/trace.ts` rides that trace id across the queue,
 * so fragmenting it would take producer-to-job continuity with it.
 *
 * `beforeSendSpan` cannot do this either. Its signature is
 * `(span: SpanJSON) => SpanJSON`, with no null in the return type, so it can
 * rewrite a span but never drop one.
 *
 * That leaves this hook, which runs once the trace is already assembled and
 * changes nothing about its structure: read the route off the child that has
 * it, rename the event, drop the children that were only ever duplicates of
 * the root.
 *
 * Pure, and typed structurally rather than against `@sentry/core`, for the same
 * reason as `scrub.ts`: it is shared with the browser bundle and must be
 * testable without an SDK.
 */

type SpanLike = { op?: string; data?: Record<string, unknown> };

type TransactionLike = {
  transaction?: string;
  transaction_info?: { source?: string };
  contexts?: { trace?: { op?: string; data?: Record<string, unknown> } };
  spans?: SpanLike[];
};

const SERVER_OP = "http.server";

function stringAttribute(
  data: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  const value = data?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function beforeSendTransaction<T extends TransactionLike>(event: T): T {
  try {
    const root = event.contexts?.trace;
    if (root?.op !== SERVER_OP) return event;

    const method = stringAttribute(root.data, "http.method");
    const route = event.spans
      ?.filter((span) => span.op === SERVER_OP)
      .map((span) => stringAttribute(span.data, "http.route"))
      .find((value) => value !== undefined);

    // No route means nothing better to call it, and the duplicates stay: they
    // are the only place the route could still turn up.
    if (!method || !route) return event;

    return {
      ...event,
      transaction: `${method} ${route}`,
      transaction_info: { source: "route" },
      spans: event.spans?.filter((span) => span.op !== SERVER_OP),
    };
  } catch {
    return event;
  }
}
